import { useEffect, useState } from "react";
import { 
  FileText, Search, ExternalLink, 
  Copy, MessageSquare, Send, Euro, Check, X, ShieldAlert
} from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

export default function Quotes() {
  const { user, tenant } = useAuth();
  // Seul le gérant (owner) ou le superadmin peut enregistrer les montants chiffrés sur-mesure
  const isOwner = user?.role === "owner" || user?.role === "superadmin" || !user?.role;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedQuote, setSelectedQuote] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState("");

  const loadQuotes = () => {
    setLoading(true);
    API.get("/quotes")
      .then((res) => setQuotes(res.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadQuotes();
  }, []);

  useEffect(() => {
    if (selectedQuote) {
      setEstimatedPrice(selectedQuote.estimated_amount !== null && selectedQuote.estimated_amount !== undefined ? selectedQuote.estimated_amount : "");
      loadMessages(selectedQuote.id);
    } else {
      setMessages([]);
    }
  }, [selectedQuote]);

  const loadMessages = (qid) => {
    API.get(`/quotes/${qid}/messages`)
      .then((res) => setMessages(res.data))
      .catch((err) => console.error("Erreur messages:", err));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedQuote) return;

    setSendingMsg(true);
    try {
      const res = await API.post(`/quotes/${selectedQuote.id}/messages`, {
        content: messageInput.trim(),
        channel: "web",
      });
      setMessages((prev) => [...prev, res.data]);
      setMessageInput("");
      
      if (selectedQuote.status === "pending") {
        updateStatus(selectedQuote.id, "sent", false);
      }
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(formatApiError(err, "Échec de l'envoi"));
    } finally {
      setSendingMsg(false);
    }
  };

  const updateStatus = async (quoteId, newStatus, showToast = true) => {
    try {
      await API.patch(`/quotes/${quoteId}`, { status: newStatus });
      if (showToast) toast.success("Statut mis à jour");
      setQuotes((prev) =>
        prev.map((q) => (q.id === quoteId ? { ...q, status: newStatus } : q))
      );
      if (selectedQuote && selectedQuote.id === quoteId) {
        setSelectedQuote((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error(formatApiError(err, "Échec de la mise à jour"));
    }
  };

  const saveEstimatedPrice = async () => {
    if (!selectedQuote || !isOwner) return;
    try {
      const priceNum = estimatedPrice === "" ? null : parseFloat(estimatedPrice);
      await API.patch(`/quotes/${selectedQuote.id}`, { estimated_amount: priceNum });
      toast.success("Montant estimé sauvegardé");
      setSelectedQuote((prev) => ({ ...prev, estimated_amount: priceNum }));
      setQuotes((prev) =>
        prev.map((q) => (q.id === selectedQuote.id ? { ...q, estimated_amount: priceNum } : q))
      );
    } catch (err) {
      toast.error(formatApiError(err, "Erreur enregistrement chiffrage"));
    }
  };

  const quoteUrl = `${window.location.origin}/quote/${tenant?.slug || ""}`;

  const filtered = quotes.filter((q) => {
    const matchesStatus = statusFilter === "all" || q.status === statusFilter;
    const query = search.toLowerCase();
    const matchesSearch =
      q.customer_name?.toLowerCase().includes(query) ||
      q.title?.toLowerCase().includes(query) ||
      q.description?.toLowerCase().includes(query) ||
      q.item_reference?.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="rounded-full bg-amber-50 border border-amber-200/60 px-2 py-0.5 text-[11px] font-semibold text-amber-700">À traiter</span>;
      case "sent":
        return <span className="rounded-full bg-blue-50 border border-blue-200/60 px-2 py-0.5 text-[11px] font-semibold text-blue-700">En échange</span>;
      case "accepted":
        return <span className="rounded-full bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Validé</span>;
      case "rejected":
        return <span className="rounded-full bg-rose-50 border border-rose-200/60 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Refusé</span>;
      default:
        return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{status}</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Demandes de devis</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isOwner ? "Chiffrez vos prestations et échangez directement avec vos prospects." : "Suivi des demandes clients et échanges en direct."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(quoteUrl);
              toast.success("Lien de devis copié !");
            }}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Copier lien public
          </Button>
          <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Voir page devis
            </Button>
          </a>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: "Tous" },
            { id: "pending", label: "À traiter" },
            { id: "sent", label: "En échange" },
            { id: "accepted", label: "Validés" },
            { id: "rejected", label: "Refusés" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                statusFilter === f.id
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par client, objet..."
            className="pl-9 h-9 text-xs"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Colonne gauche : Dossiers récents */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:col-span-5">
          <div className="border-b border-slate-100 p-3.5 bg-slate-50/70 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Dossiers récents ({filtered.length})</span>
            {isOwner && <span className="text-[11px] text-slate-400">Montants estimatifs inclus</span>}
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Chargement des devis...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="mx-auto h-9 w-9 text-slate-300" />
              <p className="mt-2 text-sm font-medium text-slate-700">Aucun devis trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[650px] overflow-y-auto">
              {filtered.map((q) => {
                const isSelected = selectedQuote?.id === q.id;
                const hasAmount = q.estimated_amount !== null && q.estimated_amount !== undefined;
                return (
                  <div
                    key={q.id}
                    onClick={() => setSelectedQuote(q)}
                    className={`p-3.5 cursor-pointer transition-all ${
                      isSelected
                        ? "bg-blue-50/70 border-l-4 border-blue-600 pl-3"
                        : "hover:bg-slate-50/80"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-xs text-slate-900 truncate">
                          {q.title || "Demande de devis"}
                        </p>
                        <p className="text-[11px] text-slate-500 truncate mt-0.5">
                          {q.customer_name} {q.item_reference && `· ${q.item_reference}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {getStatusBadge(q.status)}
                        {isOwner && (
                          hasAmount ? (
                            <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-50/90 border border-emerald-200/70 px-1.5 py-0.5 rounded">
                              {Number(q.estimated_amount).toLocaleString("fr-FR")} €
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic">Non chiffré</span>
                          )
                        )}
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-slate-600 line-clamp-1">{q.description}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Colonne droite : Détail & Chat */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col lg:col-span-7 min-h-[580px]">
          {selectedQuote ? (
            <div className="flex flex-col h-full">
              <div className="border-b border-slate-200 p-4 bg-slate-50/40">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className="font-display text-base font-bold text-slate-900">{selectedQuote.title}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Client : <strong className="text-slate-700">{selectedQuote.customer_name}</strong> · Tél : <a href={`tel:${selectedQuote.customer_phone}`} className="text-blue-600 underline">{selectedQuote.customer_phone}</a>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => updateStatus(selectedQuote.id, "accepted")}
                    >
                      <Check className="mr-1 h-3.5 w-3.5" /> Valider
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => updateStatus(selectedQuote.id, "rejected")}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Refuser
                    </Button>
                  </div>
                </div>

                {/* Bloc montant chiffré (Affiché uniquement pour le gérant owner) */}
                {isOwner ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200/60 bg-white -mx-4 -mb-4 p-4 rounded-b-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                        <Euro className="h-3.5 w-3.5 text-emerald-600" /> Montant chiffré (€) :
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        value={estimatedPrice}
                        onChange={(e) => setEstimatedPrice(e.target.value)}
                        placeholder="Ex: 450"
                        className="h-8 w-28 text-xs"
                      />
                      <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={saveEstimatedPrice}>
                        Enregistrer
                      </Button>
                    </div>
                    {selectedQuote.item_reference && (
                      <span className="text-xs text-slate-500">
                        Réf : <strong className="text-slate-700">{selectedQuote.item_reference}</strong>
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 flex items-center justify-between pt-3 border-t border-slate-200/60 bg-white -mx-4 -mb-4 p-4 rounded-b-lg text-xs text-slate-500">
                    <span className="flex items-center gap-1.5 italic">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-500" /> Chiffrage tarifaire géré par la direction
                    </span>
                    {selectedQuote.item_reference && (
                      <span>Réf : <strong className="text-slate-700">{selectedQuote.item_reference}</strong></span>
                    )}
                  </div>
                )}
              </div>

              {/* Discussion */}
              <div className="flex-1 p-4 overflow-y-auto space-y-3 max-h-[380px]">
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3.5 text-xs text-slate-800">
                  <p className="font-semibold text-blue-900 mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5" /> Demande initiale du prospect :
                  </p>
                  <p className="whitespace-pre-wrap leading-relaxed">{selectedQuote.description}</p>
                </div>

                {messages.map((m) => {
                  const isAdmin = m.sender_type === "admin";
                  const isSystem = m.sender_type === "system";
                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col ${isSystem ? "items-center my-2" : isAdmin ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl p-3 text-xs leading-relaxed shadow-sm ${
                          isSystem
                            ? "bg-slate-100 text-slate-600 text-center border border-slate-200"
                            : isAdmin
                            ? "bg-blue-600 text-white rounded-br-none"
                            : "bg-slate-100 text-slate-800 rounded-bl-none"
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      </div>
                      <span className="mt-1 text-[10px] text-slate-400">
                        {m.sender_name} · {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleSendMessage} className="border-t border-slate-200 p-3 bg-slate-50 flex gap-2">
                <Textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Écrivez un message de suivi ou une précision..."
                  rows={2}
                  className="text-xs bg-white resize-none"
                />
                <Button
                  type="submit"
                  disabled={sendingMsg || !messageInput.trim()}
                  className="h-auto px-4 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 p-8 text-center text-slate-400">
              <MessageSquare className="h-10 w-10 text-slate-200 mb-2" />
              <p className="text-sm font-medium">Sélectionnez un devis à gauche</p>
              <p className="text-xs text-slate-400 mt-0.5">Vous pourrez consulter le dossier et échanger avec le client.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}